import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Table,
  InputNumber,
  message,
  Row,
  Col,
  Typography,
  Breadcrumb,
  Tag,
  Space,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
const { Title, Text } = Typography;

const LowStock = () => {
  const navigate = useNavigate();
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxQty, setMaxQty] = useState(10);
  const [messageApi, contextHolder] = message.useMessage();

const fetchLowStock = async () => {
  setLoading(true);

  try {
    const res = await api.get("/api/inventories/low-stock", {
      params: {
        maxQuantity: maxQty,
      },
    });

    setLowStockItems(res.data || []);
  } catch (err) {
    console.error("Low stock fetch error:", err);

    messageApi.error(
      err?.response?.data?.message ||
        "Failed to fetch low stock items"
    );
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchLowStock();
  }, []);

  const exportToExcel = () => {
    const data = lowStockItems.map((item) => {
      const sparepart = item.sparepart || {};
      const warehouse = item.warehouse || {};
      return {
        'Inventory ID': item.id,
        'Sparepart ID': sparepart.id || '',
        'Importer ID': sparepart.importerId || '',
        'Part Name': sparepart.partName || '',
        'Part Number': sparepart.partNumber || '',
        'Measure Unit': sparepart.measureUnit || '',
        'Current Qty': item.quantity,
        'Unit Price': sparepart.price || 0,
        'Warehouse': warehouse.name || '',
        'Warehouse Address': warehouse.address || '',
        'SKU': item.sku || '',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Low Stock Items');

    worksheet['!cols'] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 30 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `Low_Stock_Items_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columns = [
    {
      title: 'Importer ID',
      dataIndex: ['sparepart', 'importerId'],
      key: 'importerId',
      render: (val) => <Text strong>{val || '—'}</Text>,
    },
    {
      title: 'Part Name',
      dataIndex: ['sparepart', 'partName'],
      key: 'partName',
      render: (val) => <Text>{val || '—'}</Text>,
    },
    {
      title: 'Part Number',
      dataIndex: ['sparepart', 'partNumber'],
      key: 'partNumber',
    },
    {
      title: 'UOM',
      dataIndex: ['sparepart', 'measureUnit'],
      key: 'measureUnit',
      render: (u) => u || 'pcs',
    },
    {
      title: 'Unit Price',
      dataIndex: ['sparepart', 'price'],
      key: 'price',
      render: (price) => <Text strong>${(price || 0).toFixed(2)}</Text>,
    },
    {
      title: 'Current Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (qty) => (
        <Tag color={qty <= 5 ? 'red' : qty <= 10 ? 'orange' : 'gold'}>
          {qty}
        </Tag>
      ),
    },
    {
      title: 'Warehouse',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouse',
      render: (val) => val || '—',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (val) => val || '—',
    },
  ];

  const totalValue = lowStockItems.reduce((sum, item) => {
    const price = item.sparepart?.price || 0;
    return sum + item.quantity * price;
  }, 0);

  return (
    <div>
      {contextHolder}
   

      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <ExclamationCircleOutlined style={{ fontSize: 32, color: '#faad14' }} />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Low Stock Items
                </Title>
                <Text type="secondary">
                  Monitor inventory items that need restocking
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Text strong>Show items with qty ≤</Text>
              <InputNumber
                min={1}
                value={maxQty}
                onChange={(v) => setMaxQty(v || 10)}
                style={{ width: 100 }}
              />
              <Button
                type="primary"
                icon={<FilterOutlined />}
                loading={loading}
                onClick={fetchLowStock}
              >
                Load
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={lowStockItems.length === 0}
              >
                Export Excel
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Low Stock Items</Text>
              <Title level={3} style={{ margin: 0, color: '#faad14' }}>
                {lowStockItems.length}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Total Value</Text>
              <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                ${totalValue.toFixed(2)}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Threshold</Text>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                ≤ {maxQty}
              </Title>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">Status</Text>
              <Title level={3} style={{ margin: 0, color: '#ff4d4f' }}>
                {lowStockItems.length > 0 ? 'Action Needed' : 'OK'}
              </Title>
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          style={{ marginBottom: 16, background: '#fafafa' }}
          extra={
            <Space>
              <Text type="secondary">
                Showing {lowStockItems.length} items with quantity ≤ {maxQty}
              </Text>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={lowStockItems}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: loading ? (
                <Spin size="small" />
              ) : (
                <div>
                  <ExclamationCircleOutlined style={{ fontSize: 32, color: '#d9d9d9' }} />
                  <p style={{ marginTop: 8, color: '#999' }}>No low stock items found</p>
                </div>
              ),
            }}
          />
        </Card>
      </Card>
    </div>
  );
};

export default LowStock;
